import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import theme, { rem } from '../../utils/theme'
import AlertMessage from '../AlertMessage'
import ButtonWithLink from '../ButtonWithLink'
import DropDown from '../FormElement/DropDown'
import InputField from '../InputField'
import { ACKC } from './ackc'

const StyledVoucherCode = styled.div`
  width: 100%;
  background-color: ${theme.status.infoBG};
  border: 1px solid ${theme.status.infoBorder};
  border-radius: 4px;
  padding: ${rem(10)} ${rem(14)} ${rem(16)};
  margin-top: ${rem(9)};
`

const StyledTitle = styled.div`
  font-size: ${theme.typography.fontSizeSm};
  font-weight: ${theme.typography.fontWeightBolder};
  color: ${`var(--gameday-body-textColorDark, ${theme.body.textColorDark})`};
  margin-bottom: ${rem(8)};
  margin-top: ${rem(6)};
`

const StyledVoucherAdded = styled.div`
  display: flex;

  .success-message {
    padding: ${rem(8)} ${rem(16)};
  }
`

const StyledVoucherAddedMessage = styled(AlertMessage)`
  flex: 1;
  padding: ${rem(8)} ${rem(16)};
`

const StyledVoucherAddedCode = styled.div`
  font-size: ${theme.typography.fontSizeTinyish};
  margin-bottom: ${rem(-2)};
`

const StyledVoucherAddedLabel = styled.div`
  font-size: ${theme.typography.fontSizeMicro};
`

const StyledDelete = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(32)};
  margin-left: ${rem(18)};
`

const StyledInputs = styled.div`
  display: flex;
  align-items: flex-end;

  ${theme.breakpoints.down('xs')} {
    flex-flow: row wrap;
  }
`

const StyledInput = styled.div`
  ${theme.breakpoints.down('xs')} {
    width: 100%;

    & + & {
      margin-top: ${rem(8)};
    }
  }

  ${theme.breakpoints.up('sm')} {
    flex: 1;

    & + & {
      margin-left: ${rem(8)};
    }
  }

  .content-wrapper {
    padding: 0 !important;
  }

  ${props =>
    props.hasError &&
    css`
      .label,
      input {
        color: ${theme.status.errorColor} !important;
      }
      .custom-textfield {
        border-color: ${theme.status.errorBorder} !important;
      }
    `}
`

const StyledButton = styled.div`
  ${theme.breakpoints.down('xs')} {
    width: 100%;
    margin-top: ${rem(10)};
  }

  ${theme.breakpoints.up('sm')} {
    margin-left: ${rem(8)};
  }
`

const StyledError = styled.div`
  font-size: ${theme.typography.fontSizeTinyish};
  font-weight: ${theme.typography.fontWeightBolder};
  color: ${theme.status.errorColor};
  margin-top: ${rem(10)};
`

const VoucherCode = ({
  memberId,
  voucherData = {},
  voucherProviders = [],
  appliedVoucherCodes = [],
  onApplyVoucher,
  onRemoveVoucher,
  buttonColour,
  buttonTextColour,
  disableInputField = false,
}) => {
  const [voucherCode, setVoucherCode] = useState('')
  const [selectedProviderId, setSelectedProviderId] = useState('')
  const [inputError, setInputError] = useState(null)

  const selectedProvider = voucherProviders[0]?.value?.find(p => p.id === selectedProviderId)

  const memberVoucherData = voucherData[memberId]
  const { appliedVoucher, message, isLoading } = memberVoucherData || {}
  const hasAppliedVoucher = !!appliedVoucher

  const { issuer } = selectedProvider || {}
  const selectProviderDisabled = !voucherProviders.length || isLoading
  const inputDisabled = disableInputField || !selectedProvider || !issuer || isLoading
  const buttonDisabled = disableInputField || inputDisabled || voucherCode.length === 0
  const hasError = !!message || !!inputError
  const errorMessage = hasError ? message || inputError : ''

  const validateVoucherInput = (value = '') => {
    const isAlphaNumeric = str => /^[a-z0-9]+$/gi.test(str)
    const hasSpaces = str => str.indexOf(' ') >= 0

    if (appliedVoucherCodes.includes(value)) {
      setInputError('This voucher code has already been used. Please try a different one.')
      return false
    }

    if (issuer === 'NSW') {
      if (!isAlphaNumeric(value) || hasSpaces(value)) {
        setInputError('Invalid Voucher Code: Voucher Code must contain no spaces and be numerical values only.')
        return false
      }
    }

    if (issuer === 'VIC') {
      const voucherMatch = value
        .replace(/\s+/g, '')
        .toUpperCase()
        .match(/^(G\d{1,2})-?(\d{7})-?([A-Z0-9]{4})$/)

      if (!voucherMatch) {
        setInputError(
          'Invalid Voucher Code: Thanks for entering your voucher code. Unfortunately, this voucher code is not valid. Please try a different one. Voucher Format is XXX-XXXXXXX-XXXX'
        )
        return false
      }
    }

    setInputError(null)
    return true
  }

  const handleChangeProvider = providerId => {
    setInputError(null)
    setVoucherCode('')
    setSelectedProviderId(providerId)
  }

  const handleChangeValue = value => {
    setInputError(null)
    setVoucherCode(value)
  }

  const handleClickApply = () => {
    setInputError(null)
    const isValid = validateVoucherInput(voucherCode)
    if (isValid) {
      onApplyVoucher({
        memberId,
        selectedProvider,
        voucherCode,
      })
    }
  }

  return (
    <StyledVoucherCode>
      <StyledTitle>Have a voucher code?</StyledTitle>
      {issuer === 'NSW' && <ACKC />}
      {hasAppliedVoucher ? (
        <StyledVoucherAdded>
          <StyledVoucherAddedMessage variant="success" show>
            <StyledVoucherAddedCode>Voucher Code: #{appliedVoucher}</StyledVoucherAddedCode>
            <StyledVoucherAddedLabel>Successfully Applied</StyledVoucherAddedLabel>
          </StyledVoucherAddedMessage>
          <StyledDelete>
            <IconButton size="small" onClick={() => onRemoveVoucher(memberId)} disabled={disableInputField}>
              <DeleteIcon fontSize="inherit" color="secondary" disableRipple />
            </IconButton>
          </StyledDelete>
        </StyledVoucherAdded>
      ) : (
        <StyledInputs>
          {voucherProviders ? (
            <StyledInput>
              <DropDown
                label="Voucher Provider"
                defaultValue="Please Select..."
                filterData={voucherProviders}
                onChange={handleChangeProvider}
                selected={selectedProviderId}
                disabled={selectProviderDisabled}
                fullWidth
                border
                data-testid="voucherProvider"
              />
            </StyledInput>
          ) : null}
          <StyledInput hasError={hasError}>
            <InputField
              label="Voucher Code"
              placeholder="Please enter..."
              fullWidth
              disabled={inputDisabled}
              onChange={e => handleChangeValue(e.target.value)}
              value={voucherCode}
              hasError={hasError}
              isClearable
              handleOnClear={() => handleChangeValue('')}
              withBorder
              dataTestId="voucherCodeInput"
            />
          </StyledInput>
          <StyledButton>
            <ButtonWithLink
              onClick={handleClickApply}
              buttoncolour={buttonColour}
              buttontextcolour={buttonTextColour}
              disabled={buttonDisabled}
              label="Apply"
              data-testid="voucherCodeApplyButton"
            />
          </StyledButton>
        </StyledInputs>
      )}
      {hasError ? <StyledError>{errorMessage}</StyledError> : null}
    </StyledVoucherCode>
  )
}

export default VoucherCode
